"use client";

import { useEffect, useRef, useState } from 'react';

const whatsappNumbers = [
  { number: '+58 424 575 6868', link: 'https://wa.me/584245756868', label: 'Principal' },
  { number: '+58 251 418 1160', link: 'https://wa.me/582514181160', label: 'Oficina Barquisimeto' },
];

const services = [
  'Ingeniería Electrónica',
  'Software & Web',
  'Mantenimiento Técnico',
  'Seguridad y Control',
  'Automatización Industrial',
  'Telecomunicaciones',
  'Diseño e Impresión 3D',
  'Otro / Consulta general',
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal-hidden');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submit — connect to backend/email service here
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  return (
    <section
      id="contacto"
      ref={sectionRef}
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ background: 'linear-gradient(180deg, #0D1525 0%, #070B14 100%)' }}
    >
      {/* Background circuit decoration */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: 'linear-gradient(rgba(7,205,225,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(7,205,225,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <p className="section-label mb-4">// Contacto</p>
          <h2 className="font-heading font-800 text-4xl md:text-5xl text-white leading-tight tracking-tight mb-4">
            ¿<span className="gradient-text-cyan">Trabajamos</span>{' '}
            <span className="gradient-text-orange">juntos?</span>
          </h2>
          <p className="font-body text-[#8892A4] text-lg max-w-2xl mx-auto leading-relaxed">
            Cuéntanos tu proyecto. En menos de 24 horas te respondemos con una propuesta técnica inicial, sin compromiso.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left: Contact info */}
          <div className="lg:col-span-4 space-y-6 reveal-hidden">
            {/* WhatsApp cards */}
            <div>
              <p className="section-label mb-4">Escríbenos por WhatsApp</p>
              <div className="space-y-3">
                {whatsappNumbers.map((wa) => (
                  <a
                    key={wa.number}
                    href={wa.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 glass-card rounded-lg p-4 group transition-all hover:border-[#25D366]/40"
                    style={{ border: '1px solid rgba(37,211,102,0.15)' }}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-[#25D366]/20"
                      style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)' }}
                    >
                      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-[#25D366]">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <p
                        className="text-[10px] font-body tracking-widest uppercase"
                        style={{ color: 'rgba(37,211,102,0.7)' }}
                      >
                        {wa.label}
                      </p>
                      <p className="text-white font-heading font-600 text-base group-hover:text-[#25D366] transition-colors">
                        {wa.number}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Email */}
            <div className="glass-card rounded-lg p-4" style={{ border: '1px solid rgba(7,205,225,0.1)' }}>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 text-[#07CDE1]"
                  style={{ background: 'rgba(7,205,225,0.08)', border: '1px solid rgba(7,205,225,0.2)' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-body tracking-widest uppercase text-[#07CDE1]/70">Email</p>
                  <a
                    href="mailto:2023.gts.consultores@gmail.com"
                    className="text-white font-body text-sm hover:text-[#07CDE1] transition-colors"
                  >
                    2023.gts.consultores@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="glass-card rounded-lg p-4" style={{ border: '1px solid rgba(7,205,225,0.1)' }}>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 text-[#07CDE1]"
                  style={{ background: 'rgba(7,205,225,0.08)', border: '1px solid rgba(7,205,225,0.2)' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-body tracking-widest uppercase text-[#07CDE1]/70">Ubicación</p>
                  <p className="text-white font-body text-sm">
                    Barquisimeto, Estado Lara<br />
                    <span className="text-[#8892A4]">Venezuela</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div
              className="rounded-lg p-4 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(234,88,12,0.08), rgba(234,88,12,0.03))',
                border: '1px solid rgba(234,88,12,0.2)',
              }}
            >
              <p className="text-[#EA580C] font-heading font-700 text-2xl mb-1">Lun – Vie</p>
              <p className="text-white font-body text-sm">8:00 AM – 6:00 PM</p>
              <p className="text-[#8892A4] text-[11px] font-body mt-1">Horario de atención Venezuela (VET)</p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-8 reveal-hidden" style={{ transitionDelay: '0.2s' }}>
            <div
              className="glass-card rounded-xl p-8 md:p-10"
              style={{ border: '1px solid rgba(7,205,225,0.12)' }}
            >
              {submitted ? (
                <div className="text-center py-12">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ background: 'rgba(7,205,225,0.1)', border: '1px solid rgba(7,205,225,0.3)' }}
                  >
                    <svg className="w-8 h-8 text-[#07CDE1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-heading font-700 text-white text-2xl mb-3">¡Mensaje enviado!</h3>
                  <p className="font-body text-[#8892A4]">
                    Gracias por contactarnos. Te responderemos en menos de 24 horas.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="mb-6">
                    <h3 className="font-heading font-700 text-white text-xl mb-1">
                      Cuéntanos sobre tu proyecto
                    </h3>
                    <p className="font-body text-[#8892A4] text-sm">
                      Completa el formulario y te contactamos con una propuesta técnica personalizada.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-body font-500 tracking-wider uppercase text-[#8892A4] mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Carlos Rodríguez"
                        className="form-input w-full px-4 py-3 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-body font-500 tracking-wider uppercase text-[#8892A4] mb-2">
                        Correo electrónico *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="carlos@empresa.com"
                        className="form-input w-full px-4 py-3 rounded text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-body font-500 tracking-wider uppercase text-[#8892A4] mb-2">
                        Teléfono / WhatsApp
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+58 424 000 0000"
                        className="form-input w-full px-4 py-3 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-body font-500 tracking-wider uppercase text-[#8892A4] mb-2">
                        Servicio de interés *
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="form-input w-full px-4 py-3 rounded text-sm appearance-none"
                      >
                        <option value="">Selecciona un servicio</option>
                        {services.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-body font-500 tracking-wider uppercase text-[#8892A4] mb-2">
                      Describe tu proyecto *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Cuéntanos qué necesitas. Mientras más detalle, mejor podemos preparar una propuesta a tu medida..."
                      className="form-input w-full px-4 py-3 rounded text-sm resize-none"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-2">
                    <button
                      type="submit"
                      className="btn-primary flex-1 flex items-center justify-center gap-3 px-8 py-4 rounded text-base"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Enviar propuesta
                    </button>
                    <a
                      href="https://wa.me/584245756868?text=Hola%20GTS%20Consultores%2C%20me%20interesa%20hablar%20sobre%20un%20proyecto"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 px-8 py-4 rounded text-base font-heading font-600 transition-all"
                      style={{
                        background: 'rgba(37,211,102,0.1)',
                        border: '1px solid rgba(37,211,102,0.3)',
                        color: '#25D366',
                      }}
                    >
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current flex-shrink-0">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp directo
                    </a>
                  </div>

                  <p className="text-[11px] font-body text-[#8892A4] text-center pt-2">
                    Al enviar, aceptas que nos contactemos contigo para discutir tu proyecto.
                    Nunca compartimos tu información.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}